import { useUserInfo } from '@/context/user-info';
import { useMemo } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Minus, Plus } from '@untitled-ui/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Collection } from '@/services/app.service';

export function CollectionView({ data }: { data: Collection }) {
  const { userLanguage } = useUserInfo();
  const { collectionSlug, articleSlug } = useParams();
  const navigate = useNavigate();

  const title = useMemo(
    () => data.translated_content[userLanguage]?.name ?? data.name,
    [data, userLanguage],
  );

  const isArticleOpen = (slug: string) => articleSlug === slug;

  return (
    <div className="size-full flex flex-col gap-8 lg:w-[700px]">
      <h2 className="font-phosphate text-3xl text-fg-primary">{title}</h2>
      <div>
        {data.articles.map((article) => {
          const articlePath = `/${userLanguage}/faq/${collectionSlug}/${article.slug}`;
          const open = isArticleOpen(article.slug);
          return (
            <div key={article.id} className="flex flex-col gap-4">
              <div className="flex items-center gap-6">
                <div className="size-5 flex items-center justify-center bg-bg-orange-hover rounded-sm">
                  {open ? (
                    <Minus className="size-4 text-white font-bold" />
                  ) : (
                    <Plus className="size-4 text-white font-bold" />
                  )}
                </div>
                <button
                  type="button"
                  className="w-full text-left font-bold"
                  onClick={() => {
                    if (open) {
                      navigate(`/${userLanguage}/faq/${collectionSlug}`);
                    } else {
                      navigate(articlePath);
                    }
                  }}
                >
                  {article.translated_content[userLanguage]?.title ?? article.title}
                </button>
              </div>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <Outlet />
                  </motion.div>
                )}
              </AnimatePresence>
              <hr className="border-t border-bg-muted mb-4" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
